import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { http } from "@/lib/api";
import { Navigate, Link } from "react-router-dom";
import { Key, Zap, Sparkles, ExternalLink, Crown } from "lucide-react";

const TIER_META = {
  free: { label: "Free", color: "#6B7280" },
  starter: { label: "Starter", color: "#6EE7B7" },
  creator: { label: "Creator", color: "#00FFC2" },
  mastery: { label: "Mastery", color: "#A855F7" },
};

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [courses, setCourses] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    http.get("/courses").then(({ data }) => setCourses(data.items));
    http.get("/prompts").then(({ data }) => setPrompts(data.items));
    http.get("/settings").then(({ data }) => setSettings(data));
  }, []);

  if (loading) return <div className="p-20 text-center text-gray-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const tier = user.tier || "free";
  const meta = TIER_META[tier] || TIER_META.free;
  const isPaid = tier !== "free";
  const hasCustomGpt = (tier === "creator" || tier === "mastery") && settings.custom_gpt_url;

  const savedCourses = courses.filter((c) => user.saved_courses?.includes(c.slug));
  const savedPrompts = prompts.filter((p) => user.saved_prompts?.includes(p.title));

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="dashboard-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-3">Your dashboard</div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight font-display-ai">
            Welcome back, <span className="text-[#00FFC2]">{user.name || user.email.split("@")[0]}</span>.
          </h1>
        </div>
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border hairline"
          style={{ borderColor: meta.color + "55", color: meta.color }}
          data-testid="dashboard-tier-badge"
        >
          {isPaid ? <Crown size={14} /> : <Key size={14} />}
          <span className="text-xs tracking-[0.25em] uppercase font-semibold">{meta.label}</span>
        </div>
      </div>

      {/* Top tiles */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Tier / Redeem tile */}
        {!isPaid ? (
          <div className="glass rounded-2xl p-6 col-span-1 md:col-span-2" data-testid="dashboard-redeem-tile">
            <div className="flex items-center gap-2 mb-2">
              <Key size={16} className="text-[#A855F7]" />
              <span className="label-eyebrow">Already purchased?</span>
            </div>
            <h3 className="text-2xl font-bold tracking-tight font-display-ai">
              Redeem your Gumroad license to unlock everything.
            </h3>
            <p className="text-sm text-gray-400 mt-2 leading-relaxed">
              Unlimited Prompt Lab, all 7 modules, the Toolkit, and (for Creator/Mastery) the
              private Custom GPT.
            </p>
            <div className="mt-5 flex gap-3 flex-wrap">
              <Link to="/redeem" className="btn-mint rounded-full px-5 py-2.5 text-sm" data-testid="dashboard-redeem-btn">
                Redeem license
              </Link>
              <Link to="/academy#tiers" className="rounded-full px-5 py-2.5 text-sm border hairline hover:bg-white/5">
                See tiers
              </Link>
            </div>
          </div>
        ) : (
          <div className="glass rounded-2xl p-6 col-span-1 md:col-span-2 glow-mint" data-testid="dashboard-tier-tile">
            <div className="flex items-center gap-2 mb-2">
              <Zap size={16} className="text-[#00FFC2]" />
              <span className="label-eyebrow">Active access</span>
            </div>
            <h3 className="text-2xl font-bold tracking-tight font-display-ai">
              You're on {meta.label}. Unlimited Prompt Lab is active.
            </h3>
            <p className="text-sm text-gray-400 mt-2 leading-relaxed">
              All modules unlocked. Your Toolkit downloads are available below.
              {tier === "starter" && " Upgrade to Creator or Mastery to unlock the Custom GPT."}
            </p>
            <div className="mt-5 flex gap-3 flex-wrap">
              <Link to="/academy/prompts" className="btn-mint rounded-full px-5 py-2.5 text-sm">
                Open Prompt Lab
              </Link>
              <Link to="/academy/courses" className="rounded-full px-5 py-2.5 text-sm border hairline hover:bg-white/5">
                View modules
              </Link>
            </div>
          </div>
        )}

        {/* Custom GPT tile — gated */}
        <div className={`glass rounded-2xl p-6 relative overflow-hidden ${hasCustomGpt ? "glow-purple" : ""}`} data-testid="dashboard-custom-gpt-tile">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={16} className="text-[#A855F7]" />
            <span className="label-eyebrow">Custom GPT</span>
          </div>
          <h3 className="text-lg font-bold tracking-tight">
            AI Creator OS Command Center
          </h3>
          <p className="text-xs text-gray-400 mt-2 leading-relaxed">
            Your private creator mentor inside ChatGPT — built for content systems, monetization &
            automation.
          </p>
          {hasCustomGpt ? (
            <a
              href={settings.custom_gpt_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dashboard-custom-gpt-open"
              className="mt-5 btn-purple rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2"
            >
              Open in ChatGPT <ExternalLink size={12} />
            </a>
          ) : (
            <div className="mt-5">
              <div className="text-xs text-gray-500 mb-2">
                {tier === "starter"
                  ? "Available on Creator & Mastery tiers."
                  : isPaid
                  ? "Coming soon — admin will add the link."
                  : "Available after Creator or Mastery activation."}
              </div>
              <Link to="/academy#tiers" className="text-xs text-[#A855F7] hover:underline">
                Upgrade →
              </Link>
            </div>
          )}
        </div>
      </div>

      <section className="mt-16">
        <div className="flex items-end justify-between mb-6">
          <h2 className="text-2xl font-bold">Saved modules</h2>
          <Link to="/academy/courses" className="text-sm text-gray-400 hover:text-white">All modules →</Link>
        </div>
        {savedCourses.length === 0 ? (
          <div className="glass rounded-xl p-8 text-gray-400 text-sm">
            No saved modules yet. Browse the curriculum and tap the bookmark.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {savedCourses.map((c) => (
              <article key={c.slug} className="glass rounded-xl overflow-hidden" data-testid={`saved-course-${c.slug}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#00FFC2] mb-2">{c.level}</div>
                  <div className="text-lg font-semibold">{c.title}</div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="mt-16">
        <div className="flex items-end justify-between mb-6">
          <h2 className="text-2xl font-bold">Saved prompts</h2>
          <Link to="/academy/prompts" className="text-sm text-gray-400 hover:text-white">All prompts →</Link>
        </div>
        {savedPrompts.length === 0 ? (
          <div className="glass rounded-xl p-8 text-gray-400 text-sm">No saved prompts yet. Save anything from the library.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {savedPrompts.map((p) => (
              <article key={p.title} className="glass rounded-xl p-5" data-testid={`saved-prompt-${p.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#00FFC2] mb-2">{p.category}</div>
                <div className="text-lg font-semibold">{p.title}</div>
                <p className="text-sm text-gray-400 mt-3 leading-relaxed">{p.preview}</p>
              </article>
            ))}
          </div>
        )}
      </section>

      {user.is_admin && (
        <section className="mt-16">
          <Link to="/admin" className="glass rounded-2xl p-6 flex items-center justify-between gap-6 hover:-translate-y-1 transition" data-testid="dashboard-admin-link">
            <div>
              <div className="label-eyebrow mb-1">Admin</div>
              <div className="text-lg font-semibold">Open the CMS</div>
              <div className="text-sm text-gray-400">Edit modules, tiers, prompts, blog, testimonials & view leads.</div>
            </div>
            <ExternalLink size={20} className="text-[#A855F7]" />
          </Link>
        </section>
      )}
    </div>
  );
}
